import { Carousel as AntdCarousel, Col, Row, Space } from "antd"
import { CarouselRef } from "antd/lib/carousel"
import { first } from "lodash"
import React from "react"
import { RefObject, useEffect, useRef, useState } from "react"
import InputMask from "react-input-mask"

import { Button } from 'components/Button'
import { Buttons } from "components/Card/Card"
import { ITEMS_PER_CAROUSEL_PAGE } from "containers/Survey/models"
import { ACTION, Value } from "containers/Survey/types"
import {
  get_index_of_position,
  isValueSelected,
  splitCarouselPages,
} from "containers/Survey/utils"
import { split as splitterArray } from "containers/Survey/utils"
import styles from "./styles.module.scss"
import { UiCarouselProps } from "./types"
import {
  UiArrow,
  UiCheckboxes,
  UiGenericSlide,
  UiInline,
  UiSelector,
  UiSlide,
} from "./Ui"
import { Keyboard } from "hocs/withKeyboard"
import { Input } from "components/Input"
import FatButton from "components/Button/FatButton/FatButton"
import { languages } from "locales/languages"

export const UiCarousel: React.FC<UiCarouselProps> = (props): JSX.Element => {
  const view = props.ui?.view || "carousel"
  const ref = useRef<CarouselRef>() as RefObject<CarouselRef>
  const [hide, setHide] = useState({ left: true, right: true })
  const [codeValue, setCodeValue] = useState<string>('');
  const [inputValue, setInputValue] = useState<string>('');
  const pages = splitCarouselPages(props.items, ITEMS_PER_CAROUSEL_PAGE)

  // current index carousel position
  const position = props.ui?.position
    ? get_index_of_position(props.ui?.position, pages.length)
    : 0
  // previous arrow carousel
  const previous = () => ref?.current?.prev()
  // next arrow carousel
  const next = () => ref?.current?.next()
  // before carousel change
  const onBeforeChange = (from: number, to: number): void => {
    let _hide = hide
    if (to <= 0) setHide((_hide = { ..._hide, left: true }))
    else setHide((_hide = { ..._hide, left: false }))
    if (to === pages.length - 1) setHide((_hide = { ..._hide, right: true }))
    else setHide((_hide = { ..._hide, right: false }))
  }

  useEffect(() => {
    if (props?.ui?.view === "code") {
      // reset input
      setCodeValue('');
      if (props.selected?.[0] !== undefined) {
        setCodeValue(props?.selected?.[0] as string);
      }
    }
  }, [props?.selected])

  useEffect(() => {
    if (Number(props.items.length) <= ITEMS_PER_CAROUSEL_PAGE)
      setHide({ left: true, right: true })
    else setHide({ left: true, right: false })
  }, [props.items.length])

  let displayer = null
  switch (view) {
    case "checkbox":
      displayer = pages.map((page, key) => (
        <UiCheckboxes
          key={key}
          items={page}
          selected={props.selected}
          onSelect={props.onSelect}
          unSelect={props.unSelect}
        />
      ))
      break
    case "carousel":
      displayer = (
        <>
          <UiArrow side="left" onClick={previous} hidden={hide.left} />
          <Col span={20} style={{ textAlign: "center" }}>
            <AntdCarousel
              className={props.items.length <= 2 ? styles.ScalerCarousel : ""}
              beforeChange={onBeforeChange}
              ref={ref}
              dots={false}
              initialSlide={position}
            >
              {pages.map((page, key) => {
                return (
                  <UiSlide
                    variant={props.variant}
                    key={key}
                    items={page}
                    preventAddMore={props.ui?.preventAddMore || false}
                    selected={props.selected}
                    onSelect={props.onSelect}
                    unSelect={props.unSelect}
                  />
                )
              })}
            </AntdCarousel>
          </Col>
          <UiArrow side="right" onClick={next} hidden={hide.right} />
        </>
      )
      break
    case "inline":
      displayer = (
        <div className={styles.InlineRow}>
          {props.items.map((item, i) => {
            const selected = isValueSelected(props.selected, item.value)
            return (
              <UiInline
                key={i}
                item={item}
                imgUrl={item.imgUrl}
                disabled={item.action === ACTION.nothing}
                onClick={() => {
                  selected ? props.unSelect(item) : props.onSelect(item)
                }}
                selected={selected}
              />
            )
          })}
        </div>
      )
      break
    case "button":
      displayer = (
        <Space
          size={60}
          wrap={false}
          style={{ justifyContent: "center", width: "100%", paddingTop: "50px" }}
        >
          {props.items.map((item, i) => (
            <Button
              key={i}
              type={item.ui?.type}
              onClick={() => props.onSelect(item)}
            >
              {item.title}
            </Button>
          ))}
        </Space>)
      break;
    case "selector":
      displayer = (
        <UiSelector
          items={props.items}
          onSelect={props.onSelect}
          selectedValue={first(props.selected) as Value | undefined}
        />
      )
      break
    case "code":
      const { unit, max, title } = props.items[0];
      displayer = (<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Keyboard options={{ type: 'code', unit: unit, inputName: title }} value={codeValue} onChange={(value) => {
          setCodeValue(value)
        }}>
          <InputMask key={title} mask={unit === 'm' && '9.99'} type="text" value={codeValue} alwaysShowMask={unit === 'm'} width={30} maxLength={max} name={title} readOnly style={{ width: '100%' }} />
        </Keyboard>
        <Buttons>
          <Button type="link" onClick={() => { props.onSelect({ title, value: "" }); setCodeValue('') }}>
            {languages.skipStep}
          </Button>
          <Button type="primary" onClick={() => {
            props.onSelect({ title, value: codeValue })
            setCodeValue("");
          }}>{languages.next}</Button>
        </Buttons>
      </div>);
      break;
    case "radiogroup":
      // 3 max par ligne => split en tableau de 3 => map sur les 3 lignes
      const itemByRow = props?.ui?.itemByRow || 3;
      const itemList = [];
      if (props.items.length <= 6) {
        for (let i = 0; i < props.items.length; i += itemByRow) {
          const chunk = props.items.slice(i, i + itemByRow);
          itemList.push(chunk);
        }
        displayer = (
          <div>
            {
              itemList.map((chunk) => {
                return <Space size={50} style={{ display: 'flex', flexDirection: 'row', 'alignItems': 'center', justifyContent: 'center', gap: '25px' }}> {chunk.map((item, i) => {
                  return (
                    <FatButton
                      disabled={item?.action === ACTION.nothing} additionalMentions={
                        item?.subtitle &&
                        <div>
                          <span style={{ fontSize: '16px', color: item?.action === ACTION.nothing ? '#7A7A7A' : item?.ui?.type === 'primary' ? '#fff' : '#27224F', fontWeight: 'bold' }}>{item.subtitle}</span>
                        </div>
                      }
                      type={item?.ui?.type} img={item.imgUrl} clicked={props.selected.includes(item.value)}
                      doNotFormat
                      style={{ marginTop: '50px', textAlign: 'center', minHeight: '280px' }}
                      ui={item?.ui}
                      onClick={() => props.onSelect(item)}>
                      {item.title}
                    </FatButton>)
                })}</Space>
              })
            }
          </div>)
      } else {
        displayer = (
          <>
            <UiArrow side="left" onClick={previous} hidden={hide.left} />
            <Col span={20} style={{ textAlign: "center" }}>
              <AntdCarousel
                className={props.items.length <= 2 ? styles.ScalerCarousel : ""}
                beforeChange={onBeforeChange}
                ref={ref}
                dots={false}
                initialSlide={position}
              >
                {pages.map((page, key) => {
                  return (
                    <UiSlide
                      variant={props.variant}
                      key={key}
                      items={page}
                      selected={props.selected}
                      onSelect={props.onSelect}
                      unSelect={props.unSelect}
                    />
                  )
                })}
              </AntdCarousel>
            </Col>
            <UiArrow side="right" onClick={next} hidden={hide.right} />
          </>
        )
      }
      break;
    case "input":
      const label = props?.items?.[0].title;
      displayer = (<div>
        <Keyboard options={{ type: 'text', inputName: label }} value={inputValue} onChange={(value) => {
          setInputValue(value)
        }} >
          <Input maxLength={32} type="text" value={inputValue} name={label} />
        </Keyboard>
        <Buttons>
          <Button type="link" onClick={() => { props.onSelect({ title: label, value: "" }); setInputValue('') }}>
            {languages.skipStep}
          </Button>
          <Button type="primary" onClick={() => {
            props.onSelect({ title: label, value: inputValue })
            setInputValue("");
          }}>{languages.next}</Button>
        </Buttons>
      </div>)
      break
    default:
      break
  }

  return <Row className={styles.SurveyBox}>{displayer}</Row>
}

interface CarouselProps {
  items: JSX.Element[]
  itemsPerPage?: number
  position?: number
  onChange?: <T>(item: T) => void
  toSpread?: unknown[]
  infinite?: boolean
  dots?: boolean
  style?: React.CSSProperties
  onNearEnd?: () => void
}
export const Carousel: React.FC<CarouselProps> = ({
  infinite = true,
  ...props
}): JSX.Element => {
  const ref = useRef<CarouselRef>() as RefObject<CarouselRef>
  const [hide, setHide] = useState({ left: true, right: true })
  const [pages, setPages] = useState<JSX.Element[][]>([])
  const itemsPerPage = props.itemsPerPage ?? ITEMS_PER_CAROUSEL_PAGE
  const currentSlideRef = useRef(props.position || 0)
  // Furthest page index already asked onNearEnd for, so revisiting it doesn't re-trigger.
  const triggeredNearEndForRef = useRef(-1)
  const prevItemsLengthRef = useRef(0)

  // current index carousel position
  const position = props.position || 0
  // previous arrow carousel
  const previous = () => ref?.current?.prev()
  // next arrow carousel
  const next = () => ref?.current?.next()
  // before carousel change
  const onBeforeChange = (from: number, to: number): void => {
    if (props.onChange) {
      if (props.toSpread) props.onChange(props.toSpread[to])
      else props.onChange(props.items[to])
    }
    let _hide = hide
    if (to <= 0) setHide((_hide = { ..._hide, left: true }))
    else setHide((_hide = { ..._hide, left: false }))
    if (to >= pages.length - 1) {
      setHide((_hide = { ..._hide, right: !props.onNearEnd }))
      if (to > triggeredNearEndForRef.current) {
        triggeredNearEndForRef.current = to
        props.onNearEnd?.()
      }
    } else {
      setHide((_hide = { ..._hide, right: false }))
    }
  }

  const onAfterChange = (current: number): void => {
    currentSlideRef.current = current
    if (props.onChange) {
      props.onChange(current)
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const newPages = splitterArray(props.items, itemsPerPage)
    // A shrinking item count means a new search replaced the list, not a pagination append.
    if (props.items.length < prevItemsLengthRef.current) {
      triggeredNearEndForRef.current = -1
    }
    prevItemsLengthRef.current = props.items.length
    setPages(newPages)
    setHide({
      left: currentSlideRef.current <= 0,
      right: currentSlideRef.current >= newPages.length - 1 && !props.onNearEnd,
    })
  }, [props.items])

  return (
    <Row className={styles.SurveyBox}>
      <UiArrow side="left" onClick={previous} hidden={hide.left} />
      <Col
        span={20}
        style={{
          textAlign: "center",
          height: pages.length > 1 ? "100%" : "initial",
          verticalAlign: "middle",
          ...props.style,
        }}
      >
        <AntdCarousel
          infinite={infinite}
          beforeChange={onBeforeChange}
          afterChange={onAfterChange}
          ref={ref}
          dots={props.dots ? { className: "dots-carousel" } : false}
          initialSlide={position}
        >
          {pages.map((page, key) => {
            return <UiGenericSlide key={key} items={page} />
          })}
        </AntdCarousel>
      </Col>
      <UiArrow side="right" onClick={next} hidden={hide.right} />
    </Row>
  )
}
